"use client";

import * as React from "react";
import {
  Car,
  FileText,
  DollarSign,
  Clock,
  Wrench,
  CheckCircle,
  AlertCircle,
  Package,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Badge, Skeleton } from "@/components/ui";
import { ProceduresDonutChart, Sparkline } from "@/components/dashboard";
import { formatCurrency } from "@/lib/utils";
import {
  estimatesService,
  vehiclesService,
} from "@/lib/api";
import type { ServiceEstimate, EstimateSummary } from "@/lib/types";

interface VehicleListResponse {
  vehicles: { id: string }[];
  total: number;
}

const estimateStatusConfig: Record<string, { label: string; variant: "info" | "success" | "warning" | "error" | "secondary" | "outline" }> = {
  PENDING: { label: "Pendente", variant: "warning" },
  APPROVED: { label: "Aprovado", variant: "info" },
  IN_PROGRESS: { label: "Em Andamento", variant: "info" },
  WAITING_PARTS: { label: "Aguard. Peças", variant: "warning" },
  COMPLETED: { label: "Concluído", variant: "success" },
  DELIVERED: { label: "Entregue", variant: "secondary" },
  CANCELLED: { label: "Cancelado", variant: "error" },
};

export function AutomotiveDashboard() {
  const [summary, setSummary] = React.useState<EstimateSummary | null>(null);
  const [vehicleCount, setVehicleCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchData() {
      try {
        const [summaryRes, vehiclesRes] = await Promise.all([
          estimatesService.getSummary(),
          vehiclesService.list({ limit: 1 }),
        ]);
        setSummary(summaryRes);
        setVehicleCount((vehiclesRes as VehicleListResponse).total || 0);
      } catch (error) {
        console.error("Error fetching automotive dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  const pendingCount = summary?.byStatus?.PENDING?.count || 0;
  const inProgressCount = (summary?.byStatus?.IN_PROGRESS?.count || 0) + (summary?.byStatus?.APPROVED?.count || 0);
  const completedRevenue = summary?.completed?.totalRevenue || 0;
  const completedCount = summary?.completed?.count || 0;

  const recentEstimates = summary?.recentEstimates || [];

  const statusDistribution = summary?.byStatus
    ? Object.entries(summary.byStatus)
        .filter(([, v]) => v.count > 0)
        .map(([status, data], idx) => ({
          name: estimateStatusConfig[status]?.label || status,
          value: data.count,
          color: ["#C9A87C", "#8BA888", "#A29BFE", "#F8B739", "#FF6B6B", "#6C5CE7"][idx % 6],
        }))
    : [];

  const revenueSparklineData = [
    { value: completedRevenue * 0.6 },
    { value: completedRevenue * 0.7 },
    { value: completedRevenue * 0.65 },
    { value: completedRevenue * 0.8 },
    { value: completedRevenue * 0.9 },
    { value: completedRevenue },
  ];

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-80 lg:col-span-2" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Metrics Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10">
                <AlertCircle className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-foreground-secondary">Orçamentos Pendentes</p>
                <p className="font-outfit text-3xl font-light text-foreground">
                  {pendingCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-status-info/10">
                <Wrench className="h-6 w-6 text-status-info" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-foreground-secondary">Em Andamento</p>
                <p className="font-outfit text-3xl font-light text-foreground">
                  {inProgressCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10">
                  <DollarSign className="h-6 w-6 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-foreground-secondary">Receita Concluída</p>
                  <p className="font-outfit text-3xl font-light text-foreground">
                    {formatCurrency(completedRevenue)}
                  </p>
                </div>
              </div>
              <div className="w-20">
                <Sparkline data={revenueSparklineData} color="#C9A87C" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage/10">
                <Car className="h-6 w-6 text-sage" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-foreground-secondary">Veículos Cadastrados</p>
                <p className="font-outfit text-3xl font-light text-foreground">
                  {vehicleCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Estimates + Status Distribution */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Orçamentos Recentes</CardTitle>
            <a
              href="/orcamentos"
              className="text-sm text-accent transition-colors hover:text-accent-hover"
            >
              Ver todos →
            </a>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentEstimates.length > 0 ? (
              recentEstimates.slice(0, 5).map((estimate: ServiceEstimate) => (
                <div
                  key={estimate.id}
                  className="flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-gray-50"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10">
                    <FileText className="h-5 w-5 text-accent" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      #{estimate.number}
                      {estimate.vehicle && ` - ${estimate.vehicle.brand} ${estimate.vehicle.model}`}
                    </p>
                    <p className="truncate text-xs text-foreground-secondary">
                      {estimate.description || estimate.damageType || "Sem descrição"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground">
                      {formatCurrency(Number(estimate.totalAmount))}
                    </p>
                    <Badge
                      variant={estimateStatusConfig[estimate.status]?.variant || "outline"}
                    >
                      {estimateStatusConfig[estimate.status]?.label || estimate.status}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-foreground-secondary py-8">
                Nenhum orçamento recente
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Status</CardTitle>
          </CardHeader>
          <CardContent>
            {statusDistribution.length > 0 ? (
              <ProceduresDonutChart data={statusDistribution} />
            ) : (
              <p className="text-center text-sm text-foreground-secondary py-16">
                Nenhum orçamento registrado
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      {summary?.completed && completedCount > 0 && (
        <div className="grid gap-6 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Wrench className="h-5 w-5 text-foreground-secondary" strokeWidth={1.5} />
                <div>
                  <p className="text-xs text-foreground-secondary">Mão de Obra</p>
                  <p className="font-outfit text-xl font-light text-foreground">
                    {formatCurrency(summary.completed.laborRevenue)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-foreground-secondary" strokeWidth={1.5} />
                <div>
                  <p className="text-xs text-foreground-secondary">Peças</p>
                  <p className="font-outfit text-xl font-light text-foreground">
                    {formatCurrency(summary.completed.partsRevenue)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="h-5 w-5 rounded-full bg-accent/20" />
                <div>
                  <p className="text-xs text-foreground-secondary">Pintura</p>
                  <p className="font-outfit text-xl font-light text-foreground">
                    {formatCurrency(summary.completed.paintRevenue)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-status-success" strokeWidth={1.5} />
                <div>
                  <p className="text-xs text-foreground-secondary">Serviços Concluídos</p>
                  <p className="font-outfit text-xl font-light text-foreground">
                    {completedCount}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
